import axios from 'axios';
import { List } from 'reselect/es/types';
import { getAccessToken } from '../config/supabase';
import { readSse } from './readSse';

export interface CodenamesExpected {
    expected_game_id: string;
    expected_version: number;
}

let staticClient: any = null;
let staticPromises: any = null;

/** Called with the step a long-running route is on, for the button to report. */
export type OnProgress = (message: string) => void;

export type ConcertFeedSettings = {
    city: string;
    state: string;
    playlists: string[];
    min_score: number;
    watch_artists: string[];
    ignore_artists?: string[];
    telegram_enabled: boolean;
};

export type ConcertAttendance = 'none' | 'interested' | 'going';
export type ConcertEventPreference = { favorite?: boolean; attendance?: ConcertAttendance };

export type ConcertEventSource = 'edmtrain' | 'crowdvolt' | 'dice' | 'ticketmaster';

export type ConcertFeedEvent = {
    id: string;
    name: string | null;
    link: string;
    date: string;
    end_date?: string;
    festivalInd?: boolean;
    starts_at?: string;
    timezone?: string;
    venue: { name: string; location?: string; country?: string; country_code?: string; timezone?: string };
    artistList: { name: string; link?: string; playlistCount: number }[];
    score: number;
    favorite?: boolean;
    attendance?: ConcertAttendance;
    watched: boolean;
    matched_watch: string[];
    added_at: string;
    first_indexed_at: string;
    last_checked_at: string;
    source_created_at: string | null;
    is_new: boolean;
    image_url?: string | null;
    artist_match_source?: 'lineup' | 'event_title';
    sources?: { source: ConcertEventSource; link: string; source_event_id: string; name?: string; date?: string }[];
    offers?: {
        source: ConcertEventSource;
        link: string;
        currency: string;
        price_amount: number;
        price_kind: 'resale' | 'primary';
        fees_included?: boolean;
        availability?: string;
    }[];
};

export type ConcertFeed = {
    read_only?: boolean;
    account: { spotify_user_id: string; display_name: string };
    settings: ConcertFeedSettings;
    configured: boolean;
    telegram_available: boolean;
    last_refreshed_at: string | null;
    first_refreshed_at: string | null;
    refresh_error: string | null;
    events: ConcertFeedEvent[];
    last_digest_at: string | null;
    playlists?: { id: string; name: string; track_count: number }[];
    selected_playlist_ids?: string[];
    playlist_filter_available?: boolean;
    added_within_days?: number | null;
    song_age_filter_available?: boolean;
};

export type RequestOptions = {
    signal?: AbortSignal;
    timeoutMs?: number;
};

export type PasteVisibility = 'private' | 'published';
export type PasteWritable = 'owner' | 'comment' | 'anyone';
export type PasteExpiry = 'never' | '1h' | '1d' | '7d' | '30d';

export type Paste = {
    paste_id: string;
    title: string;
    visibility: PasteVisibility;
    writable: PasteWritable;
    render_markdown: boolean;
    created_at: string;
    updated_at: string;
    /** UTC time with an offset, or null when the paste never expires. */
    expires_at: string | null;
    owner: boolean;
};

export type PasteComment = {
    comment_id: string;
    parent_id: string | null;
    quote: string;
    start_offset: number;
    end_offset: number;
    body: string;
    author_label: string;
    resolved: boolean;
    created_at: string;
    mine: boolean;
};

export type PasteInput = {
    title?: string;
    body?: string;
    render_markdown?: boolean;
    visibility?: PasteVisibility;
    writable?: PasteWritable;
    expires_in?: PasteExpiry;
};

const responseError = (body: any): string | null => {
    for (const value of [body?.error, body?.description, body?.title]) {
        if (typeof value === 'string' && value.trim()) return value;
    }
    if (body?.success === false) return 'The request failed. Try again.';
    return null;
};

export type StreamHandlers = {
    /** Called for each SSE frame, including idle heartbeats. */
    onActivity?: () => void;
    /** Called with each text chunk, in the order the model produced it. */
    onDelta?: (text: string) => void;
    /** Called with the step the backend is on, e.g. "Fetching tracks (200/900)...". */
    onProgress?: OnProgress;
    /** Called once with the terminal payload (e.g. the updated chat history). */
    onResult?: (result: any) => void;
    /** Called with every parsed frame, for routes that stream more than text (e.g. chatWithAgent). */
    onFrame?: (payload: any) => void;
    /** Stop reading the stream. Server work can continue after cancellation. */
    signal?: AbortSignal;
};

/** The screen the visitor is on. The server shows this to the model and hides it in the transcript. */
export type AgentPage = {
    path: string;
    title: string;
    product?: string;
    about?: string;
};

/** One SR Agent request: a message, or a decision on the pending action. */
export type AgentChatArgs = {
    conversation_id?: string;
    message?: string;
    decision?: { action_id: string; approved: boolean };
    visitor_id?: string;
    page?: AgentPage;
};

/**
 * The signed-in account authorizes flight operations. A passcode is only used
 * once when linking a legacy nickname to that account.
 */
export type MonikerCredentials = {
    moniker?: string | null;
    passcode?: string | null;
};

export type ScreeningReportInput = MonikerCredentials & {
    airportCode: string;
    destinationCode?: string | null;
    wasSelected?: boolean;
    occurredOn?: string | null;
    airline?: string | null;
    flightNumber?: string | null;
    note?: string | null;
    times?: number;
};

interface IClient_ {
    registerSiteVisitor: (visitorId: string) => Promise<any>;
    register: (email: string, password: string, first_name: string) => Promise<any>;
    forgotPassword: (email: string) => Promise<any>;
    verifyEmail: (token: string) => Promise<any>;
    resendEmailVerification: (email: string) => Promise<any>;
    deleteUser: () => Promise<any>;
    createNewAndSimilarPlaylist: (url: string, limit: number) => Promise<any>;
    generatePrompt: (assignment: string) => Promise<any>;
    improvePrompt: (prompt: string, assignment: string) => Promise<any>;
    getItemsFromReceipt: (img: any) => Promise<any>;
    splitBill: (items: Array<any>, users: any, ids: any, custom_total: number | null, venmo_username: string | null) => Promise<any>;
    getCrimeData: (lookback_days: number, description_substring: string, latitude: number, longitude: number, city: string) => Promise<any>;
    getCrimeTypes: (city: string) => Promise<any>;
    getCrimeCities: () => Promise<any>;
    getCrimeInsights: (city: string, lookback_days: number, description_substring: string) => Promise<any>;
    getHealthDashboard: () => Promise<any>;
    getSiteStats: () => Promise<any>;
    getCitibikeExplorer: (month?: string) => Promise<any>;
    getCitibikeStreets: (month?: string) => Promise<any>;
    getCitibikeAvailability: (station_ids: string[]) => Promise<any>;
    askSaarim: (query: string, chat_history: List) => Promise<any>;
    chatWithAgentStreaming: (args: AgentChatArgs, handlers?: StreamHandlers) => Promise<any>;
    listAgentConversations: (visitor_id?: string) => Promise<any>;
    getAgentConversation: (conversation_id: string, visitor_id?: string) => Promise<any>;
    deleteAgentConversation: (conversation_id: string, visitor_id?: string) => Promise<any>;
    chatWithCartiStreaming: (
        era: string,
        message: string,
        history: { role: string; text: string }[],
        handlers?: StreamHandlers
    ) => Promise<any>;
    initSpotifyOAuth: () => Promise<any>;
    setSpotifyToken: (code: string, state: string) => Promise<any>;
    validateSpotifyAuth: () => Promise<any>;
    getConcertFeed: (playlistIds?: string[], addedWithinDays?: number) => Promise<any>;
    setConcertEventPreference: (eventId: string, preference: ConcertEventPreference) => Promise<any>;
    setConcertIgnoredArtist: (artist: string, ignored: boolean) => Promise<any>;
    setConcertWatchedArtist: (artist: string, watched: boolean) => Promise<any>;
    saveConcertFeed: (settings: ConcertFeedSettings) => Promise<any>;
    refreshConcertFeed: (onProgress?: OnProgress) => Promise<any>;
    getSpotifyConnection: () => Promise<any>;
    getListeningStats: (time_range: string) => Promise<any>;
    disconnectSpotify: () => Promise<any>;
    findArtistEvents: (playlist_urls: string[], state: string, city: string, sort_by: string, onProgress?: OnProgress) => Promise<any>;
    createSetPlaylist: (tracklist: string, playlist_name: string, onProgress?: OnProgress, preview?: boolean) => Promise<any>;
    findDuplicates(playlist_url: string, preferences?: object, onProgress?: OnProgress, signal?: AbortSignal): Promise<any>;
    countArtistTracks(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any>;
    intersectPlaylists(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any>;
    fanSince(playlist_urls: string[], onProgress?: OnProgress, primary_artist_only?: boolean, signal?: AbortSignal): Promise<any>;
    getMySpotifyPlaylists(): Promise<any>;
    analyzePlaylist(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any>;
    segmentPlaylists(args: Record<string, unknown>, onProgress?: OnProgress, signal?: AbortSignal): Promise<any>;
    createSegmentPlaylist(
        track_ids: string[],
        playlist_name?: string,
        segment_label?: string,
        source_playlist_names?: string[],
        date_basis?: string,
        window?: string
    ): Promise<any>;
    removeDuplicates(playlist_id: string, tracks_to_remove: any[]): Promise<any>;
    createCartineseCheckoutSession: (session_type: string, trial: boolean) => Promise<any>;
    convertLatexToPdf: (latex: string, options?: RequestOptions) => Promise<any>;
    rewriteResumeSection: (resume_part: string, user_request: string) => Promise<any>;
    leaveFeedback: (feedback: string, email: string) => Promise<any>;
    logout: () => Promise<any>;
    getUser: () => Promise<any>;
    activateCartiSubscription: (session_id: string) => Promise<any>;
    getCartifyConfig: () => Promise<any>;
    cartify(input: string, config: any): Promise<any>;
    cartifyPremium: (input: string, config: any, styles?: any[]) => Promise<any>;
    listCartiConfigs: () => Promise<any>;
    saveCartiConfig: (name: string, config: Record<string, boolean | number | string>) => Promise<any>;
    deleteCartiConfig: (name: string) => Promise<any>;
    createApiKey: () => Promise<any>;
    getCartiPriceInfo: () => Promise<any>;
    cancelSubscription: (reason: string) => Promise<any>;
    createFilteredArtistPlaylist: (playlist_urls: string[], artists: string, new_playlist_name: string, onProgress?: OnProgress, preview?: boolean) => Promise<any>;
    saveBillSplit: (billSplit: any, receiptId?: string) => Promise<any>;
    getBillSplit: (receiptId: string) => Promise<any>;
    listMySplits: () => Promise<any>;
    createSplitSession: (state?: any) => Promise<any>;
    getSplitSession: (sessionId: string, options?: RequestOptions) => Promise<any>;
    updateSplitSession: (sessionId: string, patch: any, baseVersion?: number | null, options?: RequestOptions) => Promise<any>;
    createCodenamesGame: (room?: string | null, wordPack?: string, randomRoom?: boolean, theme?: string | null) => Promise<any>;
    getCodenamesGame: (room: string, playerId: string, knownVersion?: number | null) => Promise<any>;
    joinCodenamesGame: (room: string, playerId: string, seat: { name?: string; team?: string | null; role?: string }) => Promise<any>;
    leaveCodenamesGame: (room: string, playerId: string) => Promise<any>;
    giveCodenamesClue: (room: string, playerId: string, word: string, count: number, expected?: CodenamesExpected) => Promise<any>;
    revealCodenamesCard: (room: string, playerId: string, index: number, expected?: CodenamesExpected) => Promise<any>;
    endCodenamesTurn: (room: string, playerId: string, expected?: CodenamesExpected) => Promise<any>;
    newCodenamesGame: (room: string, playerId: string, wordPack?: string | null, swapTeams?: boolean, theme?: string | null, expected?: CodenamesExpected) => Promise<any>;
    getCodenamesHint: (
        room: string,
        playerId: string,
        strength: string,
        excludeClues?: string[],
        regenerate?: boolean,
        team?: string | null
    ) => Promise<any>;
    chatCodenamesCoach: (
        room: string,
        playerId: string,
        message: string,
        history?: { role: string; text: string }[],
        team?: string | null
    ) => Promise<any>;
    chatCodenamesCoachStreaming: (
        room: string,
        playerId: string,
        message: string,
        history?: { role: string; text: string }[],
        team?: string | null,
        handlers?: StreamHandlers
    ) => Promise<any>;
    getPokerCoaching: (args: {
        mode: 'review' | 'leaks' | 'ask';
        hand?: any;
        session?: any;
        hands?: any[];
        question?: string;
        history?: { role: string; text: string }[];
    }) => Promise<any>;
    askPokerCoachStreaming: (
        args: {
            question: string;
            hand?: any;
            history?: { role: string; text: string }[];
        },
        handlers?: StreamHandlers
    ) => Promise<any>;
    simulateFire: (args: Record<string, unknown>, onProgress?: OnProgress) => Promise<any>;
    getFireStrategyComparison: (args: Record<string, unknown>) => Promise<any>;
    getFireSensitivity: (args: Record<string, unknown>) => Promise<any>;
    getFireMinTcTable: (args: Record<string, unknown>) => Promise<any>;
    getFireStressTests: (args: Record<string, unknown>) => Promise<any>;
    getFireCities: () => Promise<any>;
    getRsuQuote: (ticker: string) => Promise<any>;
    simulateRsu: (args: Record<string, unknown>) => Promise<any>;
    intersectLetterboxdWatchlists: (usernames: string[]) => Promise<any>;
    discoverLetterboxdMovies: (username: string) => Promise<any>;
    planDeparture: (args: {
        commute?: string;
        direction?: string;
        text?: string;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        watch?: boolean;
        prefer_route?: string;
        prefer_margin_minutes?: number;
        from_station?: string;
        routes?: string[];
    }) => Promise<any>;
    planTrip: (args: {
        to?: string;
        destination_id?: string;
        homeward?: boolean;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        watch?: boolean;
    }) => Promise<any>;
    planJourney: (args: {
        to?: string;
        destination_id?: string;
        origin?: string;
        origin_id?: string;
        lat?: number;
        lon?: number;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        max_transfers?: number;
        watch?: boolean;
        to_label?: string;
        from_label?: string;
    }) => Promise<any>;
    searchStations: (q: string, limit?: number) => Promise<any>;
    searchPlaces: (args: {
        q: string;
        lat?: number;
        lon?: number;
        session?: string;
        limit?: number;
    }) => Promise<any>;
    resolvePlace: (args: {
        place_id: string;
        session?: string;
        label?: string;
    }) => Promise<any>;
    nearbyStations: (args: {
        lat?: number;
        lon?: number;
        at?: string;
        limit?: number;
        radius_meters?: number;
    }) => Promise<any>;
    listStationLines: (args: {
        station?: string;
        station_id?: string;
    }) => Promise<any>;
    listCommutes: () => Promise<any>;
    getHomeBoard: () => Promise<any>;
    getDeparturePlan: (watch_id: string) => Promise<any>;
    listDeparturePlans: () => Promise<any>;
    cancelDeparturePlan: (watch_id: string) => Promise<any>;
    validateDashboardAccess: (password?: string) => Promise<any>;
    getSignupsHistogram: () => Promise<any>;
    getCartiUsage: () => Promise<any>;
    getCancellationData: () => Promise<any>;
    createPlaylistFrom1001Tracklists: (url: string, onProgress?: OnProgress, preview?: boolean) => Promise<any>;
    createArtistsRecentSetsPlaylist: (artists: string, num_sets: number, new_playlist_name: string, solo_only: boolean, b2b_only: boolean, preview?: boolean) => Promise<any>;
    startCreateArtistsRecentSetsPlaylist: (artists: string, num_sets: number, new_playlist_name: string, solo_only: boolean, b2b_only: boolean, lookback_days: number | null, preview?: boolean) => Promise<any>;
    getJobStatus: (job_id: string, options?: RequestOptions) => Promise<any>;
    watchJobUntilComplete: (job_id: string, onProgress?: OnProgress, signal?: AbortSignal) => Promise<any>;
    // Randomly Selected — the airport screening map.
    claimMoniker: (args: MonikerCredentials & { pinColor?: string; pinEmoji?: string | null; displayMoniker?: string }) => Promise<any>;
    signInMoniker: (args: MonikerCredentials) => Promise<any>;
    getMyMoniker: () => Promise<any>;
    updateTravelerPin: (args: MonikerCredentials & { pinColor: string; pinEmoji?: string | null }) => Promise<any>;
    linkMonikerToAccount: (args: MonikerCredentials) => Promise<any>;
    searchAirports: (query: string, limit?: number) => Promise<any>;
    addScreeningReport: (args: ScreeningReportInput) => Promise<any>;
    parseFlightsFromText: (args: MonikerCredentials & { text: string }) => Promise<any>;
    importScreeningReports: (args: MonikerCredentials & { reports: Array<Partial<ScreeningReportInput>> }) => Promise<any>;
    updateScreeningReport: (args: MonikerCredentials & { reportId: number } & Partial<ScreeningReportInput>) => Promise<any>;
    deleteScreeningReport: (args: MonikerCredentials & { reportId: number }) => Promise<any>;
    getSelectionMap: (args?: { moniker?: string | null; sinceDays?: number | null; country?: string | null; continent?: string | null; metric?: 'selections' | 'visits' }) => Promise<any>;
    getSelectionFeed: (args?: { airportCode?: string | null; moniker?: string | null; limit?: number; sinceDays?: number | null; metric?: 'selections' | 'visits' }) => Promise<any>;
    listMyReports: (args: MonikerCredentials) => Promise<any>;
    updateTrackingStart: (args: MonikerCredentials & { trackingStartedOn: string | null }) => Promise<any>;
    getTravelerPassport: (moniker: string) => Promise<any>;
    createPaste: (input?: PasteInput) => Promise<any>;
    getPaste: (paste_id: string) => Promise<any>;
    updatePaste: (paste_id: string, input: PasteInput) => Promise<any>;
    pushPasteUpdate: (paste_id: string, input: { update: string; kind?: 'update' | 'awareness' }) => Promise<any>;
    flushPaste: (paste_id: string) => Promise<any>;
    listMyPastes: () => Promise<any>;
    deletePaste: (paste_id: string) => Promise<any>;
    createPasteComment: (paste_id: string, input: {
        body: string;
        quote?: string;
        start_offset?: number;
        end_offset?: number;
        parent_id?: string;
        author_label?: string;
    }) => Promise<any>;
    resolvePasteComment: (paste_id: string, comment_id: string, resolved?: boolean) => Promise<any>;
    deletePasteComment: (paste_id: string, comment_id: string) => Promise<any>;
}

class Client_ implements IClient_ {

    registerSiteVisitor(visitorId: string) {
        return Client_.callApi('registerSiteVisitor', { visitor_id: visitorId }, { timeoutMs: 5000 });
    }

    // TODO: add error handling
    static client_: () => IClient_ = () => {
        if (!staticClient) {
            staticClient = new Client_();
        }
        return staticClient;
    };

    static readonly baseUrl: string = process.env.NODE_ENV === 'production'
        ? 'https://api.saarim.app'
        : '';
    // static readonly baseUrl: string = '';

    // withCredentials stays on for the Spotify cookie the backend still sets;
    // user auth rides on the Authorization header attached below.
    static axiosInstance = axios.create({
        withCredentials: true,
        timeout: 180000,
    });

    static callApi = async (method?: string, args?: any, options: RequestOptions = {}) => {
        let url = `${Client_.baseUrl}/api/${method}`;

        let postBody: any = {
            method: 'POST',
            url: url,
            headers: {
                // 'Accept': 'application/json',
                // 'Content-Type': 'application/json',
                // 'Access-Control-Allow-Origin': '*',
                // 'Access-Control-Allow-Headers': '*'
            },
            data: args,
            signal: options.signal,
            timeout: options.timeoutMs,
            withCredentials: true
        };
        if (options.timeoutMs === undefined && ['getSplitSession', 'updateSplitSession', 'getCodenamesGame',
            'joinCodenamesGame', 'leaveCodenamesGame', 'giveCodenamesClue',
            'revealCodenamesCard', 'endCodenamesTurn'].includes(method ?? '')) {
            postBody.timeout = 10000;
        }

        let err: any = null;
        let res: any = null;

        try {
            await Client_.axiosInstance(postBody)
                .then((response) => {
                    res = response?.data;
                    err = responseError(res);
                    return { err, res };
                });
        } catch (error) {
            if (axios.isCancel(error)) return { err: null, res: null, cancelled: true };
            if (axios.isAxiosError(error)) res = error.response?.data ?? null;
            err = responseError(res) ?? 'The request failed. Try again.';
            const status = axios.isAxiosError(error) ? error.response?.status : undefined;
            return { err, res, status };
        }
        return { err, res };
    };

    /**
     * POST to the /stream/ sink and surface each chunk as it lands.
     *
     * Uses fetch rather than the shared axios instance: axios cannot read a
     * response body incrementally in the browser, so the request interceptor
     * that normally attaches the Supabase token does not apply here either --
     * it is attached by hand below.
     *
     * Resolves to the same { err, res } shape as callApi once the stream ends,
     * so callers can handle the terminal state exactly as they do today.
     */
    static callApiStreaming = async (
        method: string,
        args: any,
        handlers: StreamHandlers = {}
    ): Promise<{ err: any; res: any; resultReceived?: boolean; cancelled?: boolean; retryable?: boolean }> => {
        const url = `${Client_.baseUrl}/stream/${method}`;
        let err: any = null;
        let res: any = null;
        let completed = false;
        let resultReceived = false;
        let retryable = false;

        try {
            const token = await getAccessToken();
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify(args ?? {}),
                signal: handlers.signal,
            });

            // A failure before the first chunk never becomes a stream: the
            // backend still owns the headers at that point and answers in JSON.
            const contentType = response.headers.get('content-type') ?? '';
            if (response.ok === false || !contentType.includes('text/event-stream')) {
                const body = await response.json().catch(() => null);
                return {
                    err: responseError(body) ?? 'The request failed. Try again.',
                    res: body,
                    ...([408, 429].includes(response.status) || response.status >= 500 ? { retryable: true } : {}),
                };
            }

            if (!response.body) {
                return { err: 'Streaming is not supported in this browser', res: null };
            }

            await readSse(response.body, (data) => {
                handlers.onActivity?.();
                let payload: any;
                try {
                    payload = JSON.parse(data);
                } catch {
                    err = 'The response was interrupted. Try again.';
                    retryable = true;
                    return false;
                }
                if (payload.error) {
                    err = payload.error;
                    retryable = payload.retryable === true;
                    return false;
                }
                handlers.onFrame?.(payload);
                if (typeof payload.delta === 'string') handlers.onDelta?.(payload.delta);
                if (payload.progress) handlers.onProgress?.(payload.progress);
                if (Object.prototype.hasOwnProperty.call(payload, 'result')) {
                    resultReceived = true;
                    res = payload.result;
                    handlers.onResult?.(payload.result);
                }
                if (payload.done === true) {
                    completed = true;
                    return false;
                }
            });
        } catch (error: any) {
            // An aborted request is the caller cancelling on purpose, not a fault.
            if (error?.name === 'AbortError') {
                return { err: null, res, cancelled: true };
            }
            return { err: 'The connection stopped. Try again.', res, retryable: true };
        }

        if (!err && !completed) {
            err = 'The response was interrupted. Try again.';
            retryable = true;
        }
        return { err, res, resultReceived, ...(retryable ? { retryable: true } : {}) };
    };

    /**
     * Call a route over the /stream/ sink so it can report what it is doing.
     *
     * The route is the same one /api/ serves and its payload still arrives
     * whole, in the last frame. What the stream adds is the running commentary
     * a tool that takes a minute owes the person waiting on it.
     *
     * Resolves to the { err, res } shape callApi returns, so a caller only has
     * to pass onProgress.
     */
    static callApiWithProgress = async (
        method: string,
        args: any,
        onProgress?: OnProgress,
        signal?: AbortSignal
    ): Promise<{ err: any; res: any; cancelled?: boolean }> => {
        const { err, res, resultReceived, cancelled } = await Client_.callApiStreaming(method, args, { onProgress, signal });
        if (cancelled) return { err: null, res: null, cancelled: true };
        if (err) {
            return { err, res: null };
        }
        if (!resultReceived) return { err: 'The response did not include a result. Try again.', res: null };
        return { err: null, res: { success: true, result: res } };
    };

    static callApiWithFormData = async (method?: string, formData?: any) => {
        let url = `${Client_.baseUrl}/api/${method}`;
        let postBody: any = {
            method: 'POST',
            url: url,
            data: formData,
            headers: { "Content-Type": "multipart/form-data" }
        };
        let err: any = null;
        let res: any = null;

        try {
            await Client_.axiosInstance(postBody)
                .then((response) => {
                    res = response?.data;
                    err = responseError(res);
                    return { err, res };
                });
        } catch (error) {
            err = axios.isAxiosError(error)
                ? responseError(error.response?.data) ?? 'The upload failed. Try again.'
                : 'The upload failed. Try again.';
            return { err, res };
        }
        return { err, res };
    };

    static callApiAndReturnBlob = async (method?: string, args?: any, options: RequestOptions = {}) => {
        let url = `${Client_.baseUrl}/api/${method}`;

        let postBody: any = {
            method: 'POST',
            url: url,
            headers: {
                // 'Accept': 'application/json',
                // 'Content-Type': 'application/json',
                // 'Access-Control-Allow-Origin': '*',
                // 'Access-Control-Allow-Headers': '*'
            },
            data: args,
            withCredentials: true,
            responseType: 'arraybuffer',
            signal: options.signal,
            timeout: options.timeoutMs
        };

        let err: any = null;
        let res: any = null;

        try {
            const response = await Client_.axiosInstance(postBody);
            const contentType = response.headers['content-type'];

            if (contentType?.includes('application/json')) {
                const decoder = new TextDecoder('utf-8');
                const jsonString = decoder.decode(response.data);
                res = JSON.parse(jsonString);
                err = responseError(res);
            } else if (contentType) {
                res = new Blob([response.data], { type: contentType });
                err = null;
            } else {
                console.error(`Response is not a ${contentType}`);
            }
        } catch (error) {
            if (axios.isCancel(error)) return { err: null, res: null, cancelled: true };
            if (axios.isAxiosError(error) && error.response?.data) {
                try {
                    res = JSON.parse(new TextDecoder().decode(error.response.data));
                } catch { /* A failed download can also contain a non-JSON proxy response. */ }
            }
            err = responseError(res) ?? 'The request failed. Try again.';
            return { err, res };
        }
        return { err, res };
    };

    async createApiKey() {
        let args: any = {};
        return Client_.callApi('createApiKey', args);
    }

    async cartifyPremium(input: string, config: any, styles?: any[]) {
        let args: any = {
            input,
            config,
        };
        if (styles) args.styles = styles;
        return Client_.callApi('cartifyPremium', args);
    }

    async getUser() {
        let args: any = {};
        return Client_.callApi('getUser', args, { timeoutMs: 15000 });
    }

    async activateCartiSubscription(session_id: string) {
        let args: any = {
            session_id
        };
        return Client_.callApi('activateCartiSubscription', args);
    }

    async logout() {
        let args: any = {};
        return Client_.callApi('logout', args);
    }

    async leaveFeedback(feedback: string, email: string) {
        let args: any = {
            feedback,
            email
        };
        return Client_.callApi('leaveFeedback', args);
    }

    async register(email: string, password: string, first_name: string) {
        let args: any = {
            email,
            password,
            first_name
        };
        return Client_.callApi('register', args);
    }

    async forgotPassword(email: string) {
        let args: any = {
            email
        };
        return Client_.callApi('forgotPassword', args);
    }

    async verifyEmail(token: string) {
        let args: any = {
            token
        };
        return Client_.callApi('verifyEmail', args);
    }

    async resendEmailVerification(email: string) {
        let args: any = {
            email
        };
        return Client_.callApi('resendEmailVerification', args);
    }

    async createNewAndSimilarPlaylist(url: string, limit: number) {
        let args: any = {
            url,
            limit
        };
        return Client_.callApi('createNewAndSimilarPlaylist', args);
    }

    async generatePrompt(assignment: string) {
        let args: any = {
            assignment
        };
        return Client_.callApi('generatePrompt', args);
    }

    async improvePrompt(prompt: string, assignment: string) {
        let args: any = {
            prompt,
            assignment
        };
        return Client_.callApi('improvePrompt', args);
    }

    async getItemsFromReceipt(img: any) {
        // let args: any = {
        //     img
        // };
        return Client_.callApiWithFormData('getItemsFromReceipt', img);
    }

    async splitBill(
        items: Array<any>,
        users: any,
        ids: any,
        customTotal: number | null,
        venmoUsername: string | null = null
    ) {
        let args: any = {
            items,
            users,
            ids,
            custom_total: customTotal,
            venmo_username: venmoUsername
        };
        return Client_.callApi('splitBill', args);
    }

    async getCrimeData(lookback_days: number, description_substring: string, latitude: number, longitude: number, city: string) {
        let args: any = {
            lookback_days,
            description_substring,
            latitude,
            longitude,
            city
        };
        return Client_.callApi('getCrimeData', args);
    }

    async getCrimeTypes(city: string) {
        return Client_.callApi('getCrimeTypes', { city });
    }

    async getCrimeCities() {
        return Client_.callApi('getCrimeCities', {});
    }

    async getCrimeInsights(city: string, lookback_days: number, description_substring: string) {
        return Client_.callApi('getCrimeInsights', { city, lookback_days, description_substring });
    }

    async getHealthDashboard() {
        return Client_.callApi('getHealthDashboard', {});
    }

    async getSiteStats() {
        return Client_.callApi('getSiteStats', {});
    }

    async getCitibikeExplorer(month?: string) {
        return Client_.callApi('getCitibikeExplorer', month ? { month } : {});
    }

    async getCitibikeStreets(month?: string) {
        return Client_.callApi('getCitibikeStreets', month ? { month } : {});
    }

    async getCitibikeAvailability(station_ids: string[]) {
        return Client_.callApi('getCitibikeAvailability', { station_ids });
    }

    async askSaarim(query: string, chat_history: List) {
        let args: any = {
            query,
            chat_history
        };
        return Client_.callApi('askSaarim', args);
    }

    async chatWithAgentStreaming(args: AgentChatArgs, handlers?: StreamHandlers) {
        return Client_.callApiStreaming('chatWithAgent', args, handlers);
    }

    async listAgentConversations(visitor_id?: string) {
        // The API rejects a POST with no body, so a call with no arguments still sends {}.
        const args = visitor_id ? { visitor_id } : {};
        return Client_.callApi('listAgentConversations', args);
    }

    async getAgentConversation(conversation_id: string, visitor_id?: string) {
        const args = visitor_id ? { conversation_id, visitor_id } : { conversation_id };
        return Client_.callApi('getAgentConversation', args);
    }

    async deleteAgentConversation(conversation_id: string, visitor_id?: string) {
        const args = visitor_id ? { conversation_id, visitor_id } : { conversation_id };
        return Client_.callApi('deleteAgentConversation', args);
    }

    async chatWithCartiStreaming(
        era: string,
        message: string,
        history: { role: string; text: string }[],
        handlers?: StreamHandlers
    ) {
        return Client_.callApiStreaming('chatWithCarti', { era, message, history }, handlers);
    }

    async setSpotifyToken(code: string, state: string) {
        let args: any = {
            code,
            state
        };
        return Client_.callApi('setSpotifyToken', args);
    };

    async initSpotifyOAuth() {
        let args: any = {};
        return Client_.callApi('initSpotifyOAuth', args);
    };

    async validateSpotifyAuth() {
        let args: any = {};
        return Client_.callApi('validateSpotifyAuth', args);
    };

    async getConcertFeed(playlistIds?: string[], addedWithinDays?: number) {
        return Client_.callApi('getConcertFeed', {
            ...(playlistIds?.length ? { playlist_ids: playlistIds } : {}),
            ...(addedWithinDays ? { added_within_days: addedWithinDays } : {}),
        });
    }

    async setConcertEventPreference(eventId: string, preference: ConcertEventPreference) {
        return Client_.callApi('setConcertEventPreference', { event_id: eventId, preference });
    }

    async setConcertIgnoredArtist(artist: string, ignored: boolean) {
        return Client_.callApi('setConcertIgnoredArtist', { artist, ignored });
    }

    async setConcertWatchedArtist(artist: string, watched: boolean) {
        return Client_.callApi('setConcertWatchedArtist', { artist, watched });
    }

    async saveConcertFeed(settings: ConcertFeedSettings) {
        return Client_.callApi('saveConcertFeed', { settings });
    }

    async refreshConcertFeed(onProgress?: OnProgress) {
        return Client_.callApiWithProgress('refreshConcertFeed', {}, onProgress);
    }
    async getListeningStats(time_range: string) {
        return Client_.callApi('getListeningStats', { time_range });
    }

    async getSpotifyConnection() {
        return Client_.callApi('getSpotifyConnection', {});
    };

    async disconnectSpotify() {
        return Client_.callApi('disconnectSpotify', {});
    };

    async findArtistEvents(playlist_urls: string[], state: string, city: string, sort_by: string, onProgress?: OnProgress) {
        let args: any = {
            playlist_urls,
            state,
            city,
            sort_by
        };
        return Client_.callApiWithProgress('findArtistEvents', args, onProgress);
    };

    async createSetPlaylist(tracklist: string, playlist_name: string, onProgress?: OnProgress, preview: boolean = false) {
        let args: any = {
            preview,
            tracklist,
            playlist_name
        };
        return Client_.callApiWithProgress('createSetPlaylist', args, onProgress);
    };

    async findDuplicates(playlist_url: string, preferences?: object, onProgress?: OnProgress, signal?: AbortSignal) {
        let args: any = {
            playlist_url,
            preferences,
        };
        return Client_.callApiWithProgress('findDuplicates', args, onProgress, signal);
    };

    async removeDuplicates(playlist_id: string, tracks_to_remove: any[]): Promise<any> {
        let args: any = {
            playlist_id,
            tracks_to_remove,
        };
        return Client_.callApi('removeDuplicates', args);
    };

    async cartify(input: string, config: any): Promise<any> {
        let args: any = {
            input,
            premium: false,
            config,
        };
        return Client_.callApi('cartify', args);
    };

    async getCartifyConfig(): Promise<any> {
        let args: any = {};
        return Client_.callApi('getCartifyConfig', args);
    };

    async listCartiConfigs(): Promise<any> {
        return Client_.callApi('listCartiConfigs', {});
    };

    async saveCartiConfig(name: string, config: Record<string, boolean | number | string>): Promise<any> {
        return Client_.callApi('saveCartiConfig', { name, config });
    };

    async deleteCartiConfig(name: string): Promise<any> {
        return Client_.callApi('deleteCartiConfig', { name });
    };

    async countArtistTracks(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any> {
        let args: any = {
            playlist_urls,
        };
        return Client_.callApiWithProgress('countArtistTracks', args, onProgress, signal);
    };

    async intersectPlaylists(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any> {
        let args: any = {
            playlist_urls,
        };
        return Client_.callApiWithProgress('intersectPlaylists', args, onProgress, signal);
    };

    async fanSince(playlist_urls: string[], onProgress?: OnProgress, primary_artist_only = false, signal?: AbortSignal): Promise<any> {
        return Client_.callApiWithProgress('fanSince', { playlist_urls, primary_artist_only }, onProgress, signal);
    };

    async getMySpotifyPlaylists(): Promise<any> {
        return Client_.callApi('getMySpotifyPlaylists', {});
    };

    async analyzePlaylist(playlist_urls: string[], onProgress?: OnProgress, signal?: AbortSignal): Promise<any> {
        let args: any = {
            playlist_urls,
        };
        return Client_.callApiWithProgress('analyzePlaylist', args, onProgress, signal);
    };

    async segmentPlaylists(args: Record<string, unknown>, onProgress?: OnProgress, signal?: AbortSignal): Promise<any> {
        return Client_.callApiWithProgress('segmentPlaylists', args, onProgress, signal);
    };

    async createSegmentPlaylist(
        track_ids: string[],
        playlist_name?: string,
        segment_label?: string,
        source_playlist_names?: string[],
        date_basis?: string,
        window?: string
    ): Promise<any> {
        let args: any = {
            track_ids,
            playlist_name,
            segment_label,
            source_playlist_names,
            date_basis,
            window,
        };
        return Client_.callApi('createSegmentPlaylist', args);
    };

    async createCartineseCheckoutSession(session_type: string, trial: boolean = false) {
        let args: any = {
            session_type,
            trial
        };
        return Client_.callApi('createCartineseCheckoutSession', args);
    };

    async getCartiPriceInfo(): Promise<any> {
        let args: any = {};
        return Client_.callApi('getCartiPriceInfo', args);
    };

    async convertLatexToPdf(latex: string, options: RequestOptions = {}): Promise<any> {
        let args: any = {
            latex,
        };
        return Client_.callApiAndReturnBlob('convertLatexToPdf', args, { timeoutMs: 120000, ...options });
    };

    async rewriteResumeSection(resume_part: string, user_request: string): Promise<any> {
        let args: any = {
            resume_part,
            user_request,
        };
        return Client_.callApi('rewriteResumeSection', args);
    };

    async cancelSubscription(reason: string) {
        let args: any = {
            reason
        };
        return Client_.callApi('cancelSubscription', args);
    }

    async createFilteredArtistPlaylist(playlist_urls: string[], artists: string, new_playlist_name: string, onProgress?: OnProgress, preview: boolean = false) {
        let args: any = {
            preview,
            playlist_urls,
            artists,
            new_playlist_name,
        };
        return Client_.callApiWithProgress('createFilteredArtistPlaylist', args, onProgress);
    }

    async saveBillSplit(billSplit: any, receiptId?: string) {
        let args: any = {
            bill_split: billSplit,
            receipt_id: receiptId
        };
        return Client_.callApi('saveBillSplit', args);
    }

    async getBillSplit(receiptId: string) {
        let args: any = {
            receipt_id: receiptId
        };
        return Client_.callApi('getBillSplit', args);
    }

    async listMySplits() {
        return Client_.callApi('listMySplits', {});
    }

    async createSplitSession(state?: any) {
        let args: any = { state };
        return Client_.callApi('createSplitSession', args);
    }

    async getSplitSession(sessionId: string, options: RequestOptions = {}) {
        let args: any = { session_id: sessionId };
        return Client_.callApi('getSplitSession', args, { timeoutMs: 15000, ...options });
    }

    async updateSplitSession(sessionId: string, patch: any, baseVersion?: number | null, options: RequestOptions = {}) {
        let args: any = {
            session_id: sessionId,
            patch,
            base_version: baseVersion ?? null
        };
        return Client_.callApi('updateSplitSession', args, { timeoutMs: 15000, ...options });
    }

    async createCodenamesGame(
        room?: string | null,
        wordPack?: string,
        randomRoom?: boolean,
        theme?: string | null
    ) {
        let args: any = {
            room: room ?? null,
            word_pack: wordPack ?? null,
            random_room: !!randomRoom,
            theme: theme ?? null
        };
        return Client_.callApi('createCodenamesGame', args);
    }

    async getCodenamesGame(room: string, playerId: string, knownVersion?: number | null) {
        let args: any = {
            room,
            player_id: playerId,
            known_version: knownVersion ?? null
        };
        return Client_.callApi('getCodenamesGame', args);
    }

    async joinCodenamesGame(
        room: string,
        playerId: string,
        seat: { name?: string; team?: string | null; role?: string }
    ) {
        let args: any = {
            room,
            player_id: playerId,
            name: seat.name,
            team: seat.team,
            role: seat.role
        };
        return Client_.callApi('joinCodenamesGame', args);
    }

    async leaveCodenamesGame(room: string, playerId: string) {
        let args: any = { room, player_id: playerId };
        return Client_.callApi('leaveCodenamesGame', args);
    }

    async giveCodenamesClue(room: string, playerId: string, word: string, count: number, expected?: CodenamesExpected) {
        let args: any = { room, player_id: playerId, word, count, ...expected };
        return Client_.callApi('giveCodenamesClue', args);
    }

    async revealCodenamesCard(room: string, playerId: string, index: number, expected?: CodenamesExpected) {
        let args: any = { room, player_id: playerId, index, ...expected };
        return Client_.callApi('revealCodenamesCard', args);
    }

    async endCodenamesTurn(room: string, playerId: string, expected?: CodenamesExpected) {
        let args: any = { room, player_id: playerId, ...expected };
        return Client_.callApi('endCodenamesTurn', args);
    }

    async newCodenamesGame(
        room: string,
        playerId: string,
        wordPack?: string | null,
        swapTeams?: boolean,
        theme?: string | null,
        expected?: CodenamesExpected
    ) {
        let args: any = {
            room,
            player_id: playerId,
            word_pack: wordPack ?? null,
            swap_teams: !!swapTeams,
            theme: theme ?? null,
            ...expected
        };
        return Client_.callApi('newCodenamesGame', args);
    }

    async getCodenamesHint(
        room: string,
        playerId: string,
        strength: string,
        excludeClues?: string[],
        regenerate?: boolean,
        team?: string | null
    ) {
        let args: any = {
            room,
            player_id: playerId,
            strength,
            exclude_clues: excludeClues?.length ? excludeClues : null,
            regenerate: !!regenerate,
            team: team === 'red' || team === 'blue' ? team : null,
        };
        return Client_.callApi('getCodenamesHint', args);
    }

    async chatCodenamesCoach(
        room: string,
        playerId: string,
        message: string,
        history?: { role: string; text: string }[],
        team?: string | null
    ) {
        let args: any = {
            room,
            player_id: playerId,
            message,
            history: history ?? [],
            team: team === 'red' || team === 'blue' ? team : null,
        };
        return Client_.callApi('chatCodenamesCoach', args);
    }

    async chatCodenamesCoachStreaming(
        room: string,
        playerId: string,
        message: string,
        history?: { role: string; text: string }[],
        team?: string | null,
        handlers?: StreamHandlers
    ) {
        let args: any = {
            room,
            player_id: playerId,
            message,
            history: history ?? [],
            team: team === 'red' || team === 'blue' ? team : null,
        };
        return Client_.callApiStreaming('chatCodenamesCoach', args, handlers);
    }

    async getPokerCoaching(args: {
        mode: 'review' | 'leaks' | 'ask';
        hand?: any;
        session?: any;
        hands?: any[];
        question?: string;
        history?: { role: string; text: string }[];
    }) {
        return Client_.callApi('getPokerCoaching', args);
    }

    async askPokerCoachStreaming(
        args: {
            question: string;
            hand?: any;
            history?: { role: string; text: string }[];
        },
        handlers?: StreamHandlers
    ) {
        return Client_.callApiStreaming('askPokerCoach', args, handlers);
    }

    async simulateFire(args: Record<string, unknown>, onProgress?: OnProgress) {
        return Client_.callApiWithProgress('simulateFire', args, onProgress);
    }

    async getFireStrategyComparison(args: Record<string, unknown>) {
        return Client_.callApi('getFireStrategyComparison', args);
    }

    async getFireSensitivity(args: Record<string, unknown>) {
        return Client_.callApi('getFireSensitivity', args);
    }

    async getFireMinTcTable(args: Record<string, unknown>) {
        return Client_.callApi('getFireMinTcTable', args);
    }

    async getFireStressTests(args: Record<string, unknown>) {
        return Client_.callApi('getFireStressTests', args);
    }

    async getFireCities() {
        return Client_.callApi('getFireCities', {});
    }

    async getRsuQuote(ticker: string) {
        return Client_.callApi('getRsuQuote', { ticker });
    }

    async simulateRsu(args: Record<string, unknown>) {
        return Client_.callApi('simulateRsu', args);
    }

    async intersectLetterboxdWatchlists(usernames: string[]) {
        return Client_.callApi('intersectLetterboxdWatchlists', { usernames });
    }

    async discoverLetterboxdMovies(username: string) {
        return Client_.callApi('discoverLetterboxdMovies', { username });
    }

    async planDeparture(args: {
        commute?: string;
        direction?: string;
        text?: string;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        watch?: boolean;
        prefer_route?: string;
        prefer_margin_minutes?: number;
        from_station?: string;
        routes?: string[];
    }) {
        return Client_.callApi('planDeparture', args);
    }

    async planTrip(args: {
        to?: string;
        destination_id?: string;
        homeward?: boolean;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        watch?: boolean;
    }) {
        return Client_.callApi('planTrip', args);
    }

    async planJourney(args: {
        to?: string;
        destination_id?: string;
        origin?: string;
        origin_id?: string;
        lat?: number;
        lon?: number;
        in_minutes?: number;
        walk_minutes?: number;
        buffer_minutes?: number;
        max_transfers?: number;
        watch?: boolean;
        to_label?: string;
        from_label?: string;
    }) {
        return Client_.callApi('planJourney', args);
    }

    async searchStations(q: string, limit?: number) {
        return Client_.callApi('searchStations', { q, limit });
    }

    // `session` groups the keystrokes of one search into a single billable
    // autocomplete session, and resolvePlace closes it — so both calls have to
    // carry the same token. See python/app/internal/placesutils.py.
    async searchPlaces(args: {
        q: string;
        lat?: number;
        lon?: number;
        session?: string;
        limit?: number;
    }) {
        return Client_.callApi('searchPlaces', args);
    }

    async resolvePlace(args: {
        place_id: string;
        session?: string;
        label?: string;
    }) {
        return Client_.callApi('resolvePlace', args);
    }

    async nearbyStations(args: {
        lat?: number;
        lon?: number;
        at?: string;
        limit?: number;
        radius_meters?: number;
    }) {
        return Client_.callApi('nearbyStations', args);
    }

    async listStationLines(args: {
        station?: string;
        station_id?: string;
    }) {
        return Client_.callApi('listStationLines', args);
    }

    async listCommutes() {
        return Client_.callApi('listCommutes', {});
    }

    async getHomeBoard() {
        return Client_.callApi('getHomeBoard', {});
    }

    async getDeparturePlan(watch_id: string) {
        return Client_.callApi('getDeparturePlan', { watch_id });
    }

    async listDeparturePlans() {
        return Client_.callApi('listDeparturePlans', {});
    }

    async cancelDeparturePlan(watch_id: string) {
        return Client_.callApi('cancelDeparturePlan', { watch_id });
    }

    async deleteUser() {
        let args: any = {};
        return Client_.callApi('deleteUser', args);
    }

    async validateDashboardAccess(password?: string) {
        let args: any = {
            password
        };
        return Client_.callApi('validateDashboardAccess', args);
    }

    async getSignupsHistogram() {
        let args: any = {};
        return Client_.callApi('getSignupsHistogram', args);
    }

    async getCartiUsage() {
        let args: any = {};
        return Client_.callApi('getCartiUsage', args);
    }

    async getCancellationData() {
        let args: any = {};
        return Client_.callApi('getCancellationData', args);
    }

    async createPlaylistFrom1001Tracklists(url: string, onProgress?: OnProgress, preview: boolean = false) {
        let args: any = {
            preview,
            url,
        };
        return Client_.callApiWithProgress('createPlaylistFrom1001Tracklists', args, onProgress);
    }

    async createArtistsRecentSetsPlaylist(artists: string, num_sets: number, new_playlist_name: string, solo_only: boolean, b2b_only: boolean, preview: boolean = false) {
        let args: any = {
            preview,
            artists,
            num_sets,
            new_playlist_name,
            solo_only,
            b2b_only
        };
        return Client_.callApi('createArtistsRecentSetsPlaylist', args);
    }

    async startCreateArtistsRecentSetsPlaylist(artists: string, num_sets: number, new_playlist_name: string, solo_only: boolean, b2b_only: boolean, lookback_days: number | null, preview: boolean = false) {
        let args: any = {
            preview,
            artists,
            num_sets,
            new_playlist_name,
            solo_only,
            b2b_only,
            lookback_days
        };
        return Client_.callApi('startCreateArtistsRecentSetsPlaylist', args);
    }

    async getJobStatus(job_id: string, options: RequestOptions = {}) {
        let args: any = { job_id };
        return Client_.callApi('getJobStatus', args, { timeoutMs: 15000, ...options });
    }

    async claimMoniker(args: MonikerCredentials & { pinColor?: string; pinEmoji?: string | null; displayMoniker?: string }) {
        return Client_.callApi('claimMoniker', args);
    }

    async signInMoniker(args: MonikerCredentials) {
        return Client_.callApi('signInMoniker', args);
    }

    async getMyMoniker() {
        return Client_.callApi('getMyMoniker', {});
    }

    async updateTravelerPin(args: MonikerCredentials & { pinColor: string; pinEmoji?: string | null }) {
        return Client_.callApi('updateTravelerPin', args);
    }

    async linkMonikerToAccount(args: MonikerCredentials) {
        return Client_.callApi('linkMonikerToAccount', args);
    }

    async searchAirports(query: string, limit?: number) {
        return Client_.callApi('searchAirports', { query, limit });
    }

    async addScreeningReport(args: ScreeningReportInput) {
        return Client_.callApi('addScreeningReport', args);
    }

    async parseFlightsFromText(args: MonikerCredentials & { text: string }) {
        return Client_.callApi('parseFlightsFromText', args);
    }

    async importScreeningReports(args: MonikerCredentials & { reports: Array<Partial<ScreeningReportInput>> }) {
        return Client_.callApi('importScreeningReports', args);
    }

    async updateScreeningReport(args: MonikerCredentials & { reportId: number } & Partial<ScreeningReportInput>) {
        return Client_.callApi('updateScreeningReport', args);
    }

    async deleteScreeningReport(args: MonikerCredentials & { reportId: number }) {
        return Client_.callApi('deleteScreeningReport', args);
    }

    async getSelectionMap(args?: { moniker?: string | null; sinceDays?: number | null; country?: string | null; continent?: string | null; metric?: 'selections' | 'visits' }) {
        return Client_.callApi('getSelectionMap', args || {});
    }

    async getSelectionFeed(args?: { airportCode?: string | null; moniker?: string | null; limit?: number; sinceDays?: number | null; metric?: 'selections' | 'visits' }) {
        return Client_.callApi('getSelectionFeed', args || {});
    }

    async listMyReports(args: MonikerCredentials) {
        return Client_.callApi('listMyReports', args);
    }

    async updateTrackingStart(args: MonikerCredentials & { trackingStartedOn: string | null }) {
        return Client_.callApi('updateTrackingStart', args);
    }

    async getTravelerPassport(moniker: string) {
        return Client_.callApi('getTravelerPassport', { moniker });
    }

    async createPaste(input: PasteInput = {}) {
        return Client_.callApi('createPaste', input);
    }

    async getPaste(paste_id: string) {
        return Client_.callApi('getPaste', { paste_id });
    }

    async updatePaste(paste_id: string, input: PasteInput) {
        return Client_.callApi('updatePaste', { paste_id, ...input });
    }

    async pushPasteUpdate(paste_id: string, input: { update: string; kind?: 'update' | 'awareness' }) {
        return Client_.callApi('pushPasteUpdate', { paste_id, ...input });
    }

    async flushPaste(paste_id: string) {
        return Client_.callApi('flushPaste', { paste_id });
    }

    async listMyPastes() {
        return Client_.callApi('listMyPastes', {});
    }

    async deletePaste(paste_id: string) {
        return Client_.callApi('deletePaste', { paste_id });
    }

    async createPasteComment(paste_id: string, input: {
        body: string;
        quote?: string;
        start_offset?: number;
        end_offset?: number;
        parent_id?: string;
        author_label?: string;
    }) {
        return Client_.callApi('createPasteComment', { paste_id, ...input });
    }

    async resolvePasteComment(paste_id: string, comment_id: string, resolved = true) {
        return Client_.callApi('resolvePasteComment', { paste_id, comment_id, resolved });
    }

    async deletePasteComment(paste_id: string, comment_id: string) {
        return Client_.callApi('deletePasteComment', { paste_id, comment_id });
    }

    async watchJobUntilComplete(job_id: string, onProgress?: OnProgress, signal?: AbortSignal): Promise<any> {
        const sleep = (ms: number) => new Promise<void>((resolve, reject) => {
            const abort = () => {
                clearTimeout(timer);
                signal?.removeEventListener('abort', abort);
                reject(new DOMException('Request cancelled', 'AbortError'));
            };
            const timer = setTimeout(() => {
                signal?.removeEventListener('abort', abort);
                resolve();
            }, ms);
            signal?.addEventListener('abort', abort, { once: true });
            if (signal?.aborted) abort();
        });

        let retryDelay = 500;
        while (true) {
            if (signal?.aborted) throw new DOMException('Request cancelled', 'AbortError');
            const controller = new AbortController();
            let idleTimeout: ReturnType<typeof setTimeout>;
            let stalled = false;
            const resetIdleTimeout = () => {
                clearTimeout(idleTimeout);
                idleTimeout = setTimeout(() => {
                    stalled = true;
                    controller.abort();
                }, 45000);
            };
            const cancel = () => controller.abort();
            signal?.addEventListener('abort', cancel, { once: true });
            resetIdleTimeout();
            const response = await Client_.callApiStreaming('getJobStatus', { job_id }, {
                signal: controller.signal,
                onActivity: resetIdleTimeout,
                onProgress: (progress) => {
                    if (!signal?.aborted) onProgress?.(progress);
                },
            }).finally(() => {
                clearTimeout(idleTimeout);
                signal?.removeEventListener('abort', cancel);
            });
            if (signal?.aborted || (response.cancelled && !stalled)) throw new DOMException('Request cancelled', 'AbortError');
            if (!response.err && response.resultReceived) {
                return { err: null, res: { success: true, result: response.res } };
            }
            if (!response.retryable && !stalled) {
                return { err: response.err || 'The job response did not include a result.', res: { success: false } };
            }
            onProgress?.('Connection lost. Reconnecting...');
            await sleep(retryDelay + Math.floor(Math.random() * retryDelay * 0.2));
            retryDelay = Math.min(retryDelay * 2, 10000);
        }
    }
}

// Attach the current Supabase access token to every API call. getSession()
// refreshes it first if it is close to expiring, so requests never race an
// expiry, and a signed-out user simply sends no header.
Client_.axiosInstance.interceptors.request.use(async (config) => {
    const token = await getAccessToken();
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
});

export default Client_;
